import { useMemo } from "react";

/*
 * Avatar , renders a user's profile photo if present, otherwise
 * shows their first + last initials in a colored circle.
 *
 * Props:
 *   user: { name, avatar_url, ... }
 *   size: number (px). Default 32.
 *   className: extra classes
 *   ring: bool (adds a subtle ring for stacked lists)
 */

const PALETTE = [
  { bg: "bg-blue-100",    text: "text-blue-800" },
  { bg: "bg-emerald-100", text: "text-emerald-800" },
  { bg: "bg-violet-100",  text: "text-violet-800" },
  { bg: "bg-amber-100",   text: "text-amber-800" },
  { bg: "bg-rose-100",    text: "text-rose-800" },
  { bg: "bg-cyan-100",    text: "text-cyan-800" },
  { bg: "bg-fuchsia-100", text: "text-fuchsia-800" },
  { bg: "bg-teal-100",    text: "text-teal-800" },
];

function hashInt(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h << 5) - h + str.charCodeAt(i);
  return Math.abs(h);
}

function initialsFromName(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function Avatar({ user, size = 32, className = "", ring = false, testid }) {
  const name = user?.name || "";
  const initials = useMemo(() => initialsFromName(name), [name]);
  const color = useMemo(() => PALETTE[hashInt(name || "u") % PALETTE.length], [name]);
  const avatarUrl = user?.avatar_url;
  const fontPx = Math.max(10, Math.round(size * 0.36));

  const style = { width: size, height: size, fontSize: fontPx };
  const base = "rounded-full inline-flex items-center justify-center overflow-hidden select-none shrink-0 " + (ring ? "ring-2 ring-white " : "");

  if (avatarUrl) {
    return (
      <img
        src={`${process.env.REACT_APP_BACKEND_URL}${avatarUrl}`}
        alt={name}
        title={name}
        style={style}
        className={base + " object-cover border border-slate-200 " + className}
        data-testid={testid}
      />
    );
  }
  return (
    <span
      title={name}
      style={style}
      className={base + " font-semibold border border-white " + color.bg + " " + color.text + " " + className}
      data-testid={testid}
    >
      {initials}
    </span>
  );
}
